import { Children } from 'react';
import { useTokens, useTheme } from './theme';
import { Spinner } from './Spinner.jsx';

/**
 * DOM primitives ported 1:1 from the React Native InstitutionAdmin uiKit
 * (client/components/dashboard/InstitutionAdmin/uiKit.jsx). Same export NAMES
 * and prop names so panels port straight over. Each primitive is theme-aware
 * via useTokens(): colors/fonts come from the token bag (inline styles), layout
 * from Tailwind utility classes.
 */

// ---- pure format helpers (ported verbatim) --------------------------------

export const pct = (rate) => {
  return rate === null || rate === undefined ? 'N/A' : `${Math.round(rate * 100)}%`;
};
export const num = (value) => {
  return value === null || value === undefined ? 'N/A' : Math.round(value);
};
export const dollars = (cents) => {
  return cents === null || cents === undefined ? 'N/A' : `$${(cents / 100).toFixed(2)}`;
};

// ---- components -----------------------------------------------------------

export function Card({ children, className = '' }) {
  const t = useTokens();
  // cardClass carries the border shape: a hairline-topped open section on the
  // portal, a bordered tile on the console.
  return (
    <section
      className={`mb-4 ${t.cardClass} ${className}`}
      style={{ backgroundColor: t.cardBg, borderColor: t.hairline }}
    >
      {children}
    </section>
  );
}

/**
 * Section heading (an h2, the level under the page h1) with an optional
 * right-side action row. `hideTitle` drops the heading but keeps `right`, for a
 * panel whose title would repeat the page h1.
 */
export function SectionTitle({ children, right, hideTitle = false }) {
  const t = useTokens();
  const portal = useTheme() === 'portal';
  if (hideTitle) {
    return right ? <div className="flex items-center justify-end mb-3">{right}</div> : null;
  }
  return (
    <div className="flex items-center justify-between mb-3">
      <h2
        className={portal ? 'text-xl font-medium' : 'text-lg font-semibold'}
        style={{ fontFamily: t.fonts.display, color: t.heading }}
      >
        {children}
      </h2>
      {right || null}
    </div>
  );
}

export function Stat({ label, value, className = '' }) {
  const t = useTokens();
  const portal = useTheme() === 'portal';
  // PORTAL: cells of one bordered grid (see StatRow). The 1px spread shadow
  // paints the hairline into the grid gap, so an empty trailing slot stays
  // blank instead of showing a hairline-colored block. Label and value are
  // left-aligned. CONSOLE: keep the tiled look, value centered.
  // Wider min-width keeps two-word uppercase labels (e.g. STUDENT ADOPTION) from
  // wrapping onto awkward mid-phrase lines in the dense analytics strip.
  // `className` places a cell in the grid (a column span).
  // A row stretches every cell to its tallest, so the value sits a fixed
  // distance above the cell bottom (mt-auto plus a margin) and lines up across
  // the row when one label wraps. The margin is where a centered value sat
  // under a one-line label, half of (min height - padding - label line - value
  // line): portal (92 - 24 - 16.25 - 28) / 2 and, from sm, (84 - 20 - 16.25 -
  // 28) / 2; console, inside its 1px border, (92 - 2 - 16 - 15 - 28) / 2.
  // Rows of one-line labels do not move.
  return (
    <div
      className={`${portal
        ? 'flex min-h-[5.75rem] min-w-0 flex-col px-3 py-3 sm:min-h-[5.25rem] sm:py-2.5'
        : 'flex min-h-[5.75rem] min-w-[148px] flex-col mr-2 mb-2 rounded-md border px-3 py-2'} ${className}`}
      style={portal
        ? { backgroundColor: t.statBg, boxShadow: `0 0 0 1px ${t.hairline}` }
        : { backgroundColor: t.statBg, borderColor: t.border }}
    >
      <div
        className={`shrink-0 text-left uppercase leading-tight tracking-wide hyphens-none [overflow-wrap:normal] [word-break:normal] text-balance ${portal ? 'text-[13px]' : 'text-xs'}`}
        style={{ fontFamily: t.fonts.mono, color: t.muted }}
      >
        {label}
      </div>
      <div
        className={`mt-auto flex text-[22px] leading-7 tabular-nums ${portal ? 'mb-[11.875px] sm:mb-[9.875px] justify-start text-left' : 'mb-[15.5px] justify-center text-center'}`}
        style={{ fontFamily: t.fonts.mono, color: t.heading }}
      >
        {value}
      </div>
    </div>
  );
}

// Portal StatRow columns by cell count, so 3- and 4-cell rows never leave an
// orphan cell on its own line at full width. Longer rows wrap 2, 3, then 4 across.
// Exported so a loading skeleton can draw the same grid.
export const STAT_GRID_COLS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 min-[360px]:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-1 min-[360px]:grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-1 min-[360px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
};
const STAT_GRID_COLS_MANY = 'grid-cols-1 min-[360px]:grid-cols-2 sm:grid-cols-3 xl:grid-cols-4';

export function StatRow({ children }) {
  const t = useTokens();
  const portal = useTheme() === 'portal';
  if (!portal) {
    // CONSOLE keeps the tiled mosaic (correct there per DESIGN).
    return <div className="flex flex-wrap">{children}</div>;
  }
  // PORTAL renders ONE bordered grid: single outer border, rounded once, with
  // 1px gaps that each cell's hairline fills, read as internal separators.
  // toArray drops null children (conditional cells) from the count.
  const count = Children.toArray(children).length;
  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: t.border, backgroundColor: t.statBg }}
    >
      <div className={`grid gap-px ${STAT_GRID_COLS[count] || STAT_GRID_COLS_MANY}`}>{children}</div>
    </div>
  );
}

export function Button({
  label, children, onClick, disabled, loading = false, variant = 'primary', small, className = '', type = 'button',
}) {
  const t = useTokens();
  const secondary = variant === 'secondary';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`inline-flex items-center justify-center rounded-[10px] min-h-[44px] font-medium transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing} ${small ? 'px-4 text-sm' : 'px-5 text-base'} ${disabled ? 'opacity-55 cursor-not-allowed' : ''} ${className}`}
      style={{
        backgroundColor: secondary ? t.secondaryBg : t.primaryBg,
        color: secondary ? t.secondaryText : t.primaryText,
        border: `1px solid ${secondary ? t.secondaryBorder : t.primaryBorder}`,
        fontFamily: t.fonts.body,
      }}
    >
      {loading ? <Spinner size={small ? 16 : 20} /> : (label ?? children)}
    </button>
  );
}

/**
 * Text input. Supports BOTH the DOM `onChange(e)` and the RN-style
 * `onChangeText(text)` so ported panels keep working unchanged. `type` and
 * `inputMode` map through for numeric/email keyboards. An optional `label`
 * wraps the input in a <label>, so the text is visible and announced.
 * The placeholder color comes from the theme through a CSS variable, since an
 * inline style cannot reach ::placeholder.
 */
export function Field({
  value, onChange, onChangeText, placeholder, type = 'text', inputMode, className = '', autoFocus, label,
}) {
  const t = useTokens();
  const handleChange = (e) => {
    if (onChangeText) { onChangeText(e.target.value); }
    if (onChange) { onChange(e); }
  };
  const input = (
    <input
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      type={type}
      inputMode={inputMode}
      autoFocus={autoFocus}
      autoCapitalize="none"
      className={`w-full min-h-[44px] rounded-md border px-3 mb-2 text-base placeholder:text-[color:var(--field-placeholder)] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing} ${className}`}
      style={{
        backgroundColor: t.inputBg,
        color: t.inputText,
        borderColor: t.controlBorder,
        fontFamily: t.fonts.body,
        '--field-placeholder': t.placeholder,
      }}
    />
  );
  if (!label) return input;
  return (
    <label className="block">
      <span className="block text-[13px] mb-1" style={{ color: t.muted, fontFamily: t.fonts.body }}>{label}</span>
      {input}
    </label>
  );
}

// Exported so other selectable chips (e.g. Toggle) can share the same mark.
export function CheckGlyph() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5 shrink-0 mr-1.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12.5 10 17.5 19 7" />
    </svg>
  );
}

export function Pill({ label, children, active, onClick }) {
  const t = useTokens();
  const portal = useTheme() === 'portal';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!active}
      className={`inline-flex items-center min-h-[44px] rounded-full border px-4 mr-2 mb-1 text-sm capitalize transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
      style={{
        backgroundColor: active ? t.pillActiveBg : t.pillBg,
        color: active ? t.pillActiveText : t.pillText,
        borderColor: active ? t.pillActiveBorder : t.pillBorder,
        fontFamily: t.fonts.body,
      }}
    >
      {/* Portal: a check marks the selected chip, so selection is never color alone. */}
      {portal && active ? <CheckGlyph /> : null}
      {label ?? children}
    </button>
  );
}

export function Row({ left, right }) {
  const t = useTokens();
  return (
    <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: t.hairline }}>
      <div className="flex-1 pr-2 min-w-0">{left}</div>
      <div className="flex shrink-0 items-center whitespace-nowrap pl-2">{right}</div>
    </div>
  );
}

const BADGE_TONES = {
  neutral: (t) => { return { color: t.muted, backgroundColor: t.statBg }; },
  positive: (t) => { return { color: t.success, backgroundColor: t.statBg }; },
  // The below-cohort-floor pill (DESIGN.md portal states).
  sand: (t) => { return { color: t.body, backgroundColor: t.badgeSandBg }; },
};

/**
 * Status chip: one size (13px mono, uppercase), a word that carries the state,
 * and a tone. Not Pill, which is the 44px filter button.
 *
 * @param {{ tone?: 'neutral'|'positive'|'sand', label?: React.ReactNode, children?: React.ReactNode, className?: string }} props
 */
export function StatusBadge({ tone = 'neutral', label, children, className = '' }) {
  const t = useTokens();
  const toneStyle = (BADGE_TONES[tone] || BADGE_TONES.neutral)(t);
  return (
    <span
      className={`inline-flex items-center self-start shrink-0 rounded-full border px-2.5 py-1 text-[13px] leading-none uppercase tracking-wide whitespace-nowrap ${className}`}
      style={{ ...toneStyle, borderColor: t.border, fontFamily: t.fonts.mono }}
    >
      {label ?? children}
    </span>
  );
}

/**
 * A failed load, stated in words, with an optional Retry that re-runs the
 * panel's own load. Only the sentence is the alert, so the button label is not
 * announced as part of it. Show the empty-state copy only after a successful
 * empty response, never after a failure.
 *
 * @param {{ message: React.ReactNode, onRetry?: () => void, retryLabel?: string, className?: string }} props
 */
export function ErrorState({ message, onRetry, retryLabel = 'Retry', className = '' }) {
  const t = useTokens();
  return (
    <div className={`flex flex-wrap items-center gap-3 py-2 ${className}`}>
      <p role="alert" className="text-base" style={{ color: t.danger, fontFamily: t.fonts.body }}>{message}</p>
      {onRetry ? <Button variant="secondary" small label={retryLabel} onClick={onRetry} /> : null}
    </div>
  );
}

/**
 * Horizontal-scroll wrapper for a dense table: a bordered container, so a
 * clipped column reads as scrollable. Pass `label` to make it a named,
 * keyboard-focusable region (keyboard users can then scroll it).
 *
 * @param {{ children: React.ReactNode, label?: string, className?: string }} props
 */
export function ScrollTable({ children, label, className = '' }) {
  const t = useTokens();
  const regionProps = label ? { role: 'region', 'aria-label': label, tabIndex: 0 } : {};
  return (
    <div
      {...regionProps}
      className={`overflow-x-auto rounded-md border focus:outline-none focus-visible:ring-2 ${t.focusRing} ${className}`}
      style={{ borderColor: t.hairline }}
    >
      {children}
    </div>
  );
}

/** A full-sentence explanatory note: Hanken 16px, muted. Not for labels or IDs. */
export function Note({ children, className = '' }) {
  const t = useTokens();
  return (
    <p className={`text-base leading-normal ${className}`} style={{ color: t.muted, fontFamily: t.fonts.body }}>
      {children}
    </p>
  );
}
